import type { CityConfig } from "@/pages/city-template/types";

export const racineConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Racine",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "racine",
  route: "/cities/racine",
  title: "Emergency & Expedited Passport Services Racine WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Racine, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago, just 65 miles south. A+ BBB rated. Serving Racine County and Kenosha.",
  h1: "Emergency & Expedited Passport Services in Racine, WI",
  heroSubtitle: "Racine sits just 65 miles north of the Chicago Passport Agency — one of the closest Wisconsin cities to a U.S. regional agency. Fast Passport Center's registered couriers deliver your documents to Chicago so you get a new or renewed passport as fast as 24 hours without making the drive yourself.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Racine%20Wisconsin%20lakefront%20at%20golden%20hour%20with%20Lake%20Michigan%20shoreline%20and%20lighthouse%2C%20warm%20amber%20and%20golden%20tones%20reflecting%20on%20calm%20water%2C%20historic%20harbor%20district%20architecture%2C%20professional%20coastal%20photography%2C%20wide%20angle%20composition%20with%20lighthouse%20pier%20in%20foreground%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=racine-hero-001&orientation=landscape",
  heroImageAlt: "Racine Wisconsin Lake Michigan waterfront and lighthouse at golden hour",
  geoPosition: "42.7261;-87.7829",
  geoPlacename: "Racine, Wisconsin",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 65 miles from Racine — just over 1 hour via I-94 south",
  },
  nearbyCitiesZips: [
    { name: "Downtown Racine", zips: "53401, 53402, 53403" },
    { name: "Mount Pleasant", zips: "53406" },
    { name: "Caledonia", zips: "53108, 53402" },
    { name: "Sturtevant", zips: "53177" },
    { name: "Kenosha", zips: "53140, 53142, 53143" },
    { name: "Pleasant Prairie", zips: "53158" },
    { name: "Burlington", zips: "53105" },
    { name: "Union Grove", zips: "53182" },
    { name: "Waterford", zips: "53185" },
    { name: "Raymond", zips: "53108" },
    { name: "Wind Point", zips: "53402" },
    { name: "North Bay", zips: "53402" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Closest Wisconsin city to Chicago Passport Agency — just 65 miles" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Kenosha Coverage", desc: "Serving Kenosha, Pleasant Prairie, and the Wisconsin-Illinois border corridor" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Racine case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Milwaukee WI", route: "/cities/milwaukee" },
    { name: "Expedited passport in Madison WI", route: "/cities/madison-wi" },
    { name: "Urgent passport in Green Bay WI", route: "/cities/green-bay" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Chicago passport services", route: "/states/illinois" },
    { name: "Emergency passport in Naperville IL", route: "/cities/naperville" },
    { name: "Expedited passport in Joliet IL", route: "/cities/joliet" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Racine remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency — just 65 miles south of Racine. This makes Racine and Kenosha County some of the best-positioned Wisconsin communities for fast passport processing.",
  fpcOfficeDistance: "Serving all of Racine County and Kenosha County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Racine, Kenosha, and southeastern Wisconsin. From downtown Racine and Mount Pleasant to Kenosha and Pleasant Prairie — we help the Wisconsin-Illinois border corridor get their passports fast.",
  nearbyCitiesIntro: "We serve Racine and nearby communities including Mount Pleasant, Caledonia, Kenosha, Pleasant Prairie, Sturtevant, Burlington, and the entire Racine-Kenosha metro corridor.",
  howItWorksIntro: "Most Racine travelers complete everything online and ship documents overnight. At just 65 miles from Chicago, Fast Passport Center can courier your documents faster than most states — achieving some of the fastest turnaround times in Wisconsin.",
  mistakesIntro: "Racine travelers, including those commuting to Chicago and Kenosha cross-border residents, have unique passport needs. Here are the most common mistakes we see.",
  testimonialsIntro: "Racine and Kenosha travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Racine? Here are the most common ones we hear from Racine County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Closest WI city to Chicago Agency — fastest WI processing",
        "No drive or appointment needed",
        "Ideal for Racine and Kenosha travelers",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from MKE or ORD",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Only 65 miles to Chicago (1-hour drive)",
        "Still requires pre-scheduled appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Racine is only 65 miles from the Chicago Passport Agency — our couriers make same-day delivery possible, as fast as 24 hours.",
      bullets: ["Closest WI city to Chicago Agency", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in WI",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Racine adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Racine and Kenosha County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Racine and Kenosha County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Racine, Kenosha, and southeastern Wisconsin residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago Agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Racine County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Racine and Kenosha residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Racine WI", description: "Urgent passport processing for Racine, WI travelers via Chicago Passport Agency courier — just 65 miles from Racine." },
    { name: "Passport Renewal Racine WI", description: "Fast passport renewal for Racine and Kenosha County, Wisconsin residents." },
    { name: "New Passport Racine WI", description: "First-time passport application assistance for Racine and southeastern Wisconsin residents." },
    { name: "Lost Passport Racine WI", description: "Passport replacement for lost or stolen passports for Racine and Kenosha residents." },
    { name: "Child Passport Racine WI", description: "Minor passport services for Racine County families." },
    { name: "Name Change Passport Racine WI", description: "Passport name change service for Racine, WI residents." },
  ],
  testimonials: [
    {
      name: "Christine L.",
      location: "Mount Pleasant, WI",
      rating: 5,
      text: "Flight to Mexico in 3 days, realized my passport was expired. Fast Passport Center couriered to Chicago — just 65 miles away — and I had my renewed passport in 28 hours. Incredible.",
      service: "Emergency Passport",
    },
    {
      name: "Brian T.",
      location: "Kenosha, WI",
      rating: 5,
      text: "Living in Kenosha so close to the Illinois border, I figured getting a passport was easy. Wrong — Chicago still requires an appointment. Fast Passport Center handled everything for me in 4 days.",
      service: "Expedited Renewal",
    },
    {
      name: "Maria G.",
      location: "Downtown Racine",
      rating: 5,
      text: "My whole family needed passports for a Caribbean cruise out of Miami. Fast Passport Center processed all five of our passports in one week. Perfect for families.",
      service: "Family Passports",
    },
    {
      name: "Steven K.",
      location: "Sturtevant, WI",
      rating: 5,
      text: "Lost my passport right before a work conference in London. Fast Passport Center walked me through the DS-64 and got me a replacement in 2 days. Best service I've ever experienced.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "rac-1",
      name: "Racine Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "700 Lake Ave",
      city: "Racine",
      state: "WI",
      zip: "53403",
      phone: "(262) 632-5600",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "Downtown Racine, lakefront area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Racine. Appointment required. Call ahead to confirm passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "rac-2",
      name: "Racine County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "730 Wisconsin Ave",
      city: "Racine",
      state: "WI",
      zip: "53403",
      phone: "(262) 636-3208",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Racine",
      website: "https://www.racinecounty.com/government/county-clerk",
      websiteLabel: "Racine County Clerk",
      notes: "Accepts passport applications for Racine County residents. No photo services — bring photos.",
      icon: "ri-government-line",
    },
    {
      id: "rac-3",
      name: "Kenosha Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4111 7th Ave",
      city: "Kenosha",
      state: "WI",
      zip: "53140",
      phone: "(262) 657-6090",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "15 miles south of downtown Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Kenosha and Pleasant Prairie residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "rac-4",
      name: "Kenosha County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1010 56th St",
      city: "Kenosha",
      state: "WI",
      zip: "53140",
      phone: "(262) 653-3315",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "17 miles south of downtown Racine",
      website: "https://www.kenoshacounty.org/",
      websiteLabel: "Kenosha County Clerk",
      notes: "Accepts passport applications. Convenient for Kenosha County residents near the Wisconsin-Illinois border.",
      icon: "ri-government-line",
    },
    {
      id: "rac-5",
      name: "Mount Pleasant Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "9415 Northwestern Ave",
      city: "Mount Pleasant",
      state: "WI",
      zip: "53406",
      phone: "(262) 886-2500",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "5 miles west of downtown Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Mount Pleasant and Caledonia residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "rac-6",
      name: "Burlington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "375 Milwaukee Ave",
      city: "Burlington",
      state: "WI",
      zip: "53105",
      phone: "(262) 763-3880",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "22 miles west of downtown Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Burlington and western Racine County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "65 miles — 1-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking 'I'll Just Drive to Chicago'",
      description: "Even though Chicago is only 65 miles away, the Chicago Passport Agency has appointment-only access. Walk-ins are turned away. We handle the appointment and delivery so you don't have to risk a wasted trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2×2 inches with a white background. CVS and Walgreens in Racine are generally reliable, but always verify your photos before submitting.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Form",
      description: "Renewals use DS-82, first-time applications use DS-11. Even Racine County residents living close to Illinois sometimes show up with the wrong form and get sent home.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Proof of Travel Ready",
      description: "Emergency processing at Chicago requires confirmed travel documents. Have your flight itinerary or cruise booking ready before reaching out.",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport Without Both Parents",
      description: "Minors under 16 require both parents to appear, or one with notarized Form DS-3053. Kenosha and Racine County families often miss this.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Standard Processing Time",
      description: "Despite living near Chicago, Racine residents using standard USPS processing still wait 8–11 weeks. Start expedited processing at least 6 weeks before any international travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Racine or Kenosha." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to Chicago — just 65 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Racine or Kenosha address — fully trackable." },
  ],
  faqs: [
    {
      q: "How close is Racine to a passport agency?",
      a: "Racine is one of the closest Wisconsin cities to a regional passport agency. The Chicago Passport Agency is approximately 65 miles south of Racine — about a 1-hour drive via I-94. However, the agency requires pre-scheduled appointments and turns away walk-ins. Fast Passport Center's couriers handle the delivery so you don't need to make the trip or secure an appointment.",
    },
    {
      q: "Can I just drive to the Chicago Passport Agency from Racine?",
      a: "Technically yes, but the Chicago Passport Agency only accepts visitors with pre-scheduled appointments. Walk-ins are turned away regardless of how close you live. Fast Passport Center pre-secures courier submission slots, ensuring your documents are accepted on your behalf without you needing to be there.",
    },
    {
      q: "How fast can I get an emergency passport in Racine?",
      a: "Because Racine is only 65 miles from Chicago, Fast Passport Center can sometimes achieve turnaround faster than cities further from the agency. Emergency processing is typically 24–48 hours. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Kenosha have its own passport facilities?",
      a: "Kenosha has acceptance facilities including the Kenosha Main Post Office and Kenosha County Clerk for first-time applications and those requiring in-person submission. For renewals, most Kenosha residents can submit by mail through Fast Passport Center's courier service.",
    },
    {
      q: "Can I get a passport if I live near the Wisconsin-Illinois border?",
      a: "Yes. Residents of Pleasant Prairie, Kenosha, and the Wisconsin-Illinois border corridor are served by Fast Passport Center. Despite being close to Illinois, you still need a valid passport for all international travel, and the Chicago Passport Agency still requires appointments.",
    },
    {
      q: "What documents do I need for a first-time passport in Racine?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility in Racine or Kenosha County.",
    },
    {
      q: "How do I renew my passport in Racine?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround to Chicago — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "What airports do Racine travelers use for international flights?",
      a: "Racine travelers typically fly internationally through Milwaukee Mitchell (MKE, 30 miles north) or Chicago O'Hare (ORD, 65 miles south). Both airports offer extensive international routes. A valid U.S. passport is required for all international flights from both airports.",
    },
    {
      q: "How does Fast Passport Center serve Kenosha County?",
      a: "We serve all of Kenosha County including Kenosha city, Pleasant Prairie, Somers, and the Wisconsin-Illinois border corridor. Documents ship overnight from any UPS location in Kenosha. Our couriers handle the Chicago submission and we deliver your passport back to your Kenosha address.",
    },
    {
      q: "Do I need a passport for cruise departures from Chicago?",
      a: "Yes. All cruises departing from Chicago that stop at international ports (Canada, Caribbean, Bermuda, Mexico) require a valid U.S. passport. Even 'closed-loop' cruises starting and ending in Chicago strongly recommend a passport. Do not book a cruise without verifying your passport expiration.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Washington Ave", address: "1701 Washington Ave, Racine, WI 53403", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Douglas Ave", address: "3515 Douglas Ave, Racine, WI 53402", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Kenosha", address: "7221 120th Ave, Kenosha, WI 53142", hours: "Mon–Sun: 8AM–10PM" },
    { name: "FedEx Office — 6th St", address: "524 6th St, Racine, WI 53403", hours: "Mon–Fri: 7AM–8PM, Sat: 9AM–5PM" },
    { name: "UPS Store — Mount Pleasant", address: "7823 Washington Ave, Racine, WI 53406", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Kenosha", address: "5200 75th St, Kenosha, WI 53142", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Racine", address: "2229 S Green Bay Rd, Racine, WI 53406", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Racine County residents.", featured: true },
    { name: "WI DMV — Kenosha", address: "8600 Sheridan Rd, Kenosha, WI 53143", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Full-service DMV for Kenosha County residents.", featured: false },
    { name: "WI DMV — Burlington", address: "124 S Browns Lake Dr, Burlington, WI 53105", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves western Racine County and Walworth County residents.", featured: false },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "30 miles north of downtown Racine",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Racine's primary international connection point. About 35 minutes north via I-94. Passport required for all international flights.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "65 miles south of downtown Racine",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub accessible to Racine travelers. About 1-hour drive south via I-94. Extensive international routes.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "58 miles south of downtown Racine",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Slightly closer than O'Hare for some Racine and Kenosha travelers. Some international charter service available.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "Midway Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Wisconsin Department of Health Services Vital Records office. Online, mail, or in-person options for Racine County residents.", url: "https://www.dhs.wisconsin.gov/vitalrecords/birth.htm" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Wisconsin Vital Records. Required for estate and legal matters in Racine County.", url: "https://www.dhs.wisconsin.gov/vitalrecords/death.htm" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Racine County Register of Deeds at 730 Wisconsin Ave. Certified copies for name changes and passport applications.", url: "https://www.racinecounty.com/government/register-of-deeds" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Racine County Clerk of Circuit Court. Required for name change passport corrections.", url: "https://www.wicourts.gov/courts/circuit/racine.htm" },
  ],
};